import React from "react";
import axios from "axios";
import { CiEdit } from "react-icons/ci";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "./ckEditor"; // your stable custom editor class

export const base_url = process.env.REACT_APP_BASE_URL;
export const api_type = process.env.REACT_APP_API_TYPE;

export default function FounderNoteManage() {
  const [note, setNote] = React.useState(null);   // { id, founderNote } or null
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  // modal
  const [open, setOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null); // null => add
  const [html, setHtml] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [modalError, setModalError] = React.useState("");

  const editorRef = React.useRef(null);

  const authHeader = React.useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    []
  );

  const stripHtml = (htmlString) => {
    if (!htmlString) return "";
    const div = document.createElement("div");
    div.innerHTML = htmlString;
    return (div.textContent || div.innerText || "").trim();
  };

  const normalizeNote = (raw) => {
    const maybe = raw?.data?.founderNoteData ?? raw;
    if (maybe && typeof maybe === "object" && ("founderNote" in maybe || "id" in maybe)) {
      return { id: maybe.id ?? null, founderNote: maybe.founderNote ?? "" };
    }
    return null;
  };

  // Don't call load when the modal is open
  const load = React.useCallback(async () => {
    if (open) return; // Prevent loading when the modal is open
    
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(
        `${base_url}/${api_type}/cms/partner/founderNoteList`,
        { headers: authHeader }
      );
      setNote(normalizeNote(data));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load founder note");
    } finally {
      setLoading(false);
    }
  }, [authHeader, open]);

  React.useEffect(() => {
    load(); // only call load when modal is closed
  }, [load]);

  // open add
  const onAdd = () => {
    setEditingId(null);
    setHtml("");
    setModalError("");
    setOpen(true);
  };

  // open edit (prefill immediately, then refresh from server)
  const onEdit = async (id) => {
    setEditingId(id);
    setModalError("");

    // 1) Prefill from current state so user sees content instantly
    setHtml(note?.founderNote || "");
    setOpen(true);

    // 2) Fetch fresh from server and sync editor if changed (but prevent API call when modal is open)
    // if (!open) {
    //   try {
    //     const { data } = await axios.get(
    //       `${base_url}/${api_type}/cms/partner/founderNote`,
    //       { headers: authHeader }
    //     );
    //     const row = data?.data || data || {};
    //     const serverHtml = row.founderNote || "";
    //     setHtml(serverHtml);
    //   } catch (e) {
    //     setModalError(e?.response?.data?.message || e?.message || "Failed to load note");
    //   }
    // }
  };

  const onClose = () => {
    if (saving) return;
    setOpen(false);
    setEditingId(null);
    setHtml("");
    setModalError("");
    editorRef.current = null;
  };

  const onSave = async () => {
    setSaving(true);
    setModalError("");
    try {
      const payload = { founderNote: html || "" };
      if (editingId) {
        await axios.post(
          `${base_url}/${api_type}/cms/partner/founderNote`,
          payload,
          { headers: authHeader }
        );
      } else {
        await axios.post(
          `${base_url}/${api_type}/cms/partner/founderNote`,
          payload,
          { headers: authHeader }
        );
      }
      setOpen(false);
      editorRef.current = null;
      await load();
    } catch (e) {
      setModalError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Keep editor in sync when html changes (e.g., server fetch finished)
  React.useEffect(() => {
    if (open && editorRef.current) {
      const current = editorRef.current.getData();
      const target = html || "";
      if (current !== target) {
        editorRef.current.setData(target);
      }
    }
  }, [html, open]);

  const preview = stripHtml(note?.founderNote);

  return (
    <div>
      <div className="bg-white p-4 rounded-lg mb-4 shadow-md">
        <div className="flex items-center justify-between">
          <h1 className="text-[#252C32] text-[24px] font-bold">Founder Note</h1>
          {/* Hide Add if one note already exists */}
          {!note && (
            <button
              onClick={onAdd}
              className="bg-[#ffb819] px-4 py-2 rounded-lg font-montserrat"
            >
              Add New Founder Note
            </button>
          )}
        </div>
      </div>

      {/* Row table */}
      <div className="overflow-x-auto w-full">
        {error && (
          <div className="mx-5 my-2 px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
            {error}
          </div>
        )}

        {loading ? (
          <div className="p-5 text-sm text-gray-600">Loading…</div>
        ) : (
          <div className="overflow-hidden border rounded-2xl shadow-sm bg-white">
            <div className="px-4 py-3 border-b bg-gray-50/60">
              <h2 className="text-base font-semibold text-gray-900">Founder Note</h2>
            </div>

            <table className="min-w-full table-fixed">
              <colgroup>
                <col style={{ width: "85%" }} />
                <col style={{ width: "15%" }} />
              </colgroup>
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left p-3 border-b">Note (preview)</th>
                  <th className="text-center p-3 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {note ? (
                  <tr className="odd:bg-white even:bg-gray-50">
                    <td className="p-3 border-b align-top">
                      <div
                        className="text-gray-800"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        title={preview}
                      >
                        {preview || "—"}
                      </div>
                    </td>
                    <td className="p-3 border-b align-top">
                      <div className="flex justify-center">
                        <button
                          className="inline-flex items-center gap-1 px-3 py-1.5 border rounded hover:bg-gray-50"
                          onClick={() => onEdit(note.id)}
                        >
                          <CiEdit size={18} />
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td className="p-4 text-sm text-gray-500" colSpan={2}>
                      No founder note found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal with CKEditor */}
      {open && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={onClose} />

          {/* Modal */}
          <div className="relative bg-white w-[96%] max-w-3xl rounded-xl shadow-2xl overflow-hidden z-[1001]">
            {/* Header */}
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingId ? "Edit Founder Note" : "Add Founder Note"}
              </h2>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">Founder Note</span>
                <div className="border rounded-md">
                  <CKEditor
                    editor={Editor}
                    onReady={(ed) => {
                      editorRef.current = ed;
                      ed.setData(html || ""); // initial set when editor mounts
                    }}
                    onChange={(_, ed) => setHtml(ed.getData())}
                  />
                </div>
              </div>

              {modalError && (
                <div className="px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
                  {modalError}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving || !html.trim()}
                className="px-5 py-2 rounded-lg bg-[#1a73e8] text-white hover:opacity-95 disabled:opacity-60"
              >
                {saving ? (editingId ? "Saving…" : "Adding…") : (editingId ? "Save" : "Add")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor height */}
      <style>{`
        .ck-editor__editable[role="textbox"] { min-height: 180px; }
      `}</style>
    </div>
  );
}
